import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  ActivityMonitorController,
  withBootstrapSession,
  withIsLoadingActions,
} from '@mqd/bootstrap';
import { datadogRum } from '@datadog/browser-rum';
import currentUserStore from '../../../stores/CurrentUserStore.js';
import LoggedIn from './LoggedIn.js';
import useJanusRefreshToken from '../../../utils/janus-refresh-token/useJanusRefreshToken.js';
import { isAuth0Flow } from '../../../utils/authProvider.js';
import { looksLikeJwt } from '../../../utils/telemetryIdentity.js';
const ProtectedView = ({
  janusUser,
  redseaUser,
  applicationData,
  flipFlop,
  sessionIsBootstrapped,
  isLoadingActions,
}) => {
  const refreshJanusToken = useJanusRefreshToken();
  const { isAuthenticated, user: auth0user, error, logout: auth0Logout } = useAuth0();

  // Make Auth0 logout available globally for logOut.js
  useEffect(() => {
    if (auth0Logout) {
      window.auth0Logout = auth0Logout;
    }
  }, [auth0Logout]);

  const auth0Flow = isAuth0Flow();

  useEffect(() => {
    if (
      auth0Flow &&
      isAuthenticated &&
      sessionIsBootstrapped &&
      redseaUser?.data &&
      !currentUserStore.actingAsUserStore
    ) {
      // Identify the RUM session by the Auth0 subject only. Never send the access
      // token (a bearer credential) or email (PII) to Datadog. See SPD-5199.
      datadogRum.addRumGlobalContext('user', {
        id: auth0user?.sub,
      });
      currentUserStore.userStore.bootstrapSession({
        janusUser,
        redseaUser,
        applicationData,
        flipFlop,
      });
    }
    if (!auth0Flow && sessionIsBootstrapped && !currentUserStore.actingAsUserStore) {
      // In a mixed Auth0/Redsea state, bootstrap maps the Auth0 access token into
      // redsea_token. Never send a JWT-shaped value to Datadog. See SPD-5199.
      const redseaToken = redseaUser?.data?.redsea_token;
      datadogRum.addRumGlobalContext(
        'user',
        looksLikeJwt(redseaToken) ? {} : { token: redseaToken }
      );
      currentUserStore.userStore.bootstrapSession({
        janusUser,
        redseaUser,
        applicationData,
        flipFlop,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    auth0Flow,
    isAuthenticated,
    janusUser,
    redseaUser,
    applicationData,
    flipFlop,
    sessionIsBootstrapped,
  ]);

  useEffect(() => {
    if (!auth0Flow && sessionIsBootstrapped) {
      refreshJanusToken();
    }
  }, [auth0Flow, auth0user, isAuthenticated, refreshJanusToken, sessionIsBootstrapped]);

  useEffect(() => {
    // Only show loading for Redsea flow
    if (
      !auth0Flow &&
      !currentUserStore.actingAsUserStore &&
      !currentUserStore.userStore.loggedInComputed
    ) {
      isLoadingActions.setLoading(
        'Please refresh your browser if you are not automatically redirected after five seconds.'
      );
    } else {
      isLoadingActions.stopLoading();
    } // eslint-disable-next-line
  }, [
    auth0Flow,
    isLoadingActions,
    currentUserStore.actingAsUserStore,
    currentUserStore.userStore.loggedInComputed,
  ]);

  // Auth0 flow
  if (auth0Flow) {
    if (error) {
      console.error('!!AUTH0 ERROR:', error);
      return <div>Auth0 Error: {error.message}</div>;
    }

    // Wait for Auth0 data to be loaded from localStorage
    if (!redseaUser?.data || redseaUser.loading || !currentUserStore.userStore.loggedInComputed) {
      return <div>Loading user data...</div>;
    }

    // Auth0 authenticated - show actual app
    return (
      <>
        <LoggedIn />
        <ActivityMonitorController />
      </>
    );
  }

  // Redsea flow - check if user is logged in
  return currentUserStore.userStore.loggedInComputed || currentUserStore.actingAsUserStore ? (
    <>
      <LoggedIn />
      <ActivityMonitorController />
    </>
  ) : null;
};

export default withIsLoadingActions(withBootstrapSession(observer(ProtectedView)));
